import PropTypes from "prop-types";
import { useProjectDetailsContext } from "pages/project/context/ProjectDetailsContext";
import { FiClock, FiDollarSign, FiUsers } from "react-icons/fi";
import RichTextDisplay from "components/common/RichTextDisplay";
import { AppShareButton } from "components/common/AppShareButton";
import { buildUserPortalCampaignUrl } from "utils/projectsList";

function StatTile({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-500">{label}</p>
      <p className="mt-1 text-sm font-bold text-text-900">{value}</p>
    </div>
  );
}

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function ProjectOverviewTab({ isTabView = false }) {
  const { project, tabContent } = useProjectDetailsContext();

  const raisedValue = project.fundsRaisedAmount ?? 0;
  const hardCapValue = project.hardCapAmount ?? 0;
  const progressPercent =
    hardCapValue > 0 ? Math.max(0, Math.min((raisedValue / hardCapValue) * 100, 100)) : 0;
  const fillWidth = raisedValue > 0 && progressPercent < 1 ? 1 : progressPercent;

  if (isTabView) {
    return <RichTextDisplay html={tabContent.projectOverview} className="mt-3" />;
  }

  return (
    <div className="mt-3 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
            {project.industryName || "—"}
          </span>
        </div>

        {project?.id ? (
          <AppShareButton
            url={buildUserPortalCampaignUrl(project.id)}
            title={project.title || "Fortify Campaign"}
            ariaLabel="Share campaign"
            className="!rounded-full"
          />
        ) : null}
      </div>

      <div>
        <h4 className="text-2xl font-bold text-text-900">{project.title}</h4>
        <RichTextDisplay html={tabContent.projectOverview?.slice(0, 200)+"..."} className="mt-2" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2 text-sm text-text-700">
          <div className="flex items-center gap-2">
            <FiClock className="h-4 w-4 text-text-500" />
            <span>Days Left:</span>
            <strong>{project.daysLeft}</strong>
          </div>
          <div className="flex items-center gap-2">
            <FiDollarSign className="h-4 w-4 text-text-500" />
            <span>Price per Share:</span>
            <strong>{project.pricePerShare}</strong>
          </div>
        </div>
        <div className="space-y-2 text-sm text-text-700">
          <div className="flex items-center gap-2">
            <FiUsers className="h-4 w-4 text-text-500" />
            <span>Investors:</span>
            <strong>{project.investorCount}</strong>
          </div>
        </div>
      </div>

      <div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-brand-900 transition-all"
            style={{ width: `${fillWidth}%` }}
            aria-label={`Raised ${progressPercent.toFixed(1)} percent`}
          />
        </div>
        <p className="mt-3 text-3xl font-bold text-text-900">{project.fundsRaised} raised</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
      <StatTile label="Total Funds Required" value={project.totalFundsRequired} />
      <StatTile label="Hard Cap" value={project.hardCap} />
        <StatTile label="Min Investment Per Investor" value={project.minimumInvestment} />
        <StatTile label="Max Investment Per Investor" value={project.maximumInvestment} />
       
      </div>
    </div>
  );
}

ProjectOverviewTab.propTypes = {
  isTabView: PropTypes.bool,
};
